import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { authApi } from '@/api/endpoints/auth';
import { setCompanySwitchInProgress } from '@/App';
import { useAuthStore } from '@/stores/authStore';
import { useNotificationStore } from '@/stores/notificationStore';

/**
 * Switch the active company (sidebar switcher, Group → Budgets rows). Clears every cached query
 * before the store changes so no screen ever shows the previous company's data.
 */
export function useSwitchCompany() {
  const queryClient = useQueryClient();
  const { showError } = useNotificationStore();
  const activeCompanyId = useAuthStore((s) => s.activeCompanyId);
  const [switching, setSwitching] = useState(false);

  const switchCompany = async (companyId: string): Promise<boolean> => {
    if (companyId === activeCompanyId || switching) return false;
    setSwitching(true);
    setCompanySwitchInProgress(true);
    try {
      const res = await authApi.switchCompany(companyId);
      // Remove all cached queries BEFORE updating state — prevents stale
      // cross-company data from being served during the transition.
      queryClient.removeQueries();
      // Batch: update modules, permissions, and switch company atomically.
      // Always write modules/permissions from response (even if empty array)
      // to avoid stale data from previous company persisting.
      useAuthStore.setState((state) => ({
        activeCompanyId: companyId,
        companies: state.companies.map((c) =>
          c.company_id === companyId
            ? {
                ...c,
                enabled_modules: res.data.enabled_modules,
                permissions: res.data.permissions,
              }
            : c
        ),
      }));
      return true;
    } catch {
      showError('Failed to switch company. Please try again.');
      return false;
    } finally {
      setSwitching(false);
      setCompanySwitchInProgress(false);
    }
  };

  return { switchCompany, switching };
}
